import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, first, lastValueFrom } from 'rxjs';
import { selectDevicesPermissions } from '../store/selectors/devices.selectors';
import { selectIdentityManagementPermissions } from '../store/selectors/identitymanagment.selector';
import { selectLicensesPermissions } from '../store/selectors/licenses.selectors';
import { selectSalesPermissions } from '../store/selectors/sales.selector';
import { selectXpacProxyPermissions } from '../store/selectors/xpacProxy.selector';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { AllApiPermissions, ApiPermissions, IApiPermissions } from '../utils/interfaces/auth.interfaces';
import { IRemoteXPACPortalState } from '../utils/interfaces/store.interfaces';
import { selectProductionToolingPermissions } from '../store/selectors/production.selector';
import { OperationEnum } from '../utils/enums/operation.enums';
import { hasPermission } from '../utils/functions/permission.function';
import { selectAnalyticsPermissions } from '../store/selectors/analytics.selector';
import { selectInventoryManagementPermissions } from '../store/selectors/inventorymanagement.selector';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  private cachedPermissions: IApiPermissions;

  constructor(private store: Store<IRemoteXPACPortalState>) { }

  public async getPermissions(): Promise<IApiPermissions> {
    // Fetch auth info from store using an await
    const salesPermissions: ApiPermissions = await lastValueFrom(this.store.select(selectSalesPermissions)
      .pipe(
        filter(hasValue),
        first()
      ));

    const licensesPermissions: ApiPermissions = await lastValueFrom(this.store.select(selectLicensesPermissions)
      .pipe(
        filter(hasValue),
        first()
      ));

    const devicesPermissions: ApiPermissions = await lastValueFrom(this.store.select(selectDevicesPermissions)
      .pipe(
        filter(hasValue),
        first()
      ));

    const xpacProxyPermissions: ApiPermissions = await lastValueFrom(this.store.select(selectXpacProxyPermissions)
      .pipe(
        filter(hasValue),
        first()
      ));

    const identityManagementPermissions: ApiPermissions = await lastValueFrom(this.store.select(selectIdentityManagementPermissions)
      .pipe(
        filter(hasValue),
        first()
      ));

    const inventoryPermissions: ApiPermissions = await lastValueFrom(this.store.select(selectInventoryManagementPermissions)
      .pipe(
        filter(hasValue),
        first()
      ));

    const productionToolingPermissions: ApiPermissions = await lastValueFrom(this.store.select(selectProductionToolingPermissions)
      .pipe(
        filter(hasValue),
        first()
      ));

    const analyticsPermissions: ApiPermissions = await lastValueFrom(this.store.select(selectAnalyticsPermissions)
      .pipe(
        filter(hasValue),
        first()
      ));

    // Combine al the values to one big list of permissions
    let permissions: IApiPermissions = {
      RemoteXpacPermissions: devicesPermissions,
      LicensePermissions: licensesPermissions,
      IdentityPermissions: identityManagementPermissions,
      SalesPermissions: salesPermissions,
      XpacProxyPermissions: xpacProxyPermissions,
      ProductionToolingPermissions: productionToolingPermissions,
      AnalyticsPermissions: analyticsPermissions,
      InventoryPermissions: inventoryPermissions,
    };

    this.cachedPermissions = permissions;
    return permissions;
  }

  public getCachedPermissions(): IApiPermissions | null {
    return this.cachedPermissions ?? null;
  }

  public hasPermission(permission: keyof AllApiPermissions, operation: OperationEnum = OperationEnum.READ): boolean {
    if (!this.cachedPermissions) {
      return false;
    }

    return hasPermission(permission, this.cachedPermissions, operation);
  }
}